import React, { useMemo, useRef } from 'react';
import i18n from 'utils/i18n';
import { useTranslation } from 'react-i18next';
import GlassPreviewImage from 'i/glass.jpg';

import { View, Document, Page, Text, Image } from '@react-pdf/renderer';
import { dateToLocaleString, getCurrentDate, getPrevDayNextMonth } from 'utils';
import { CONFIGURATION_TYPE } from 'utils/constant';
import { PdfConfigurationCode } from '../PdfConfigurationCode';
import { PdfYourReference } from '../PdfYourReference';
import { PdfHeader } from '../PdfHeader';
import { PdfUploadedDocuments } from '../PdfUploadedDocuments';
import { PdfTotalInfo } from '../PdfTotalInfo';
import { PdfBlockHeading } from '../PdfBlockHeading';
import {
	PdfGlassConfigDetailFirst,
	PdfGlassConfigDetailSecond,
	PdfGlassConfigTable,
} from '../PdfGlassConfig';
import { styles } from '../styles';

const splitPositions = (
	positions,
	isFirstOverallPage,
	firstPageRows,
	otherFirstPageRows,
	subsequentPageRows
) => {
	const initialRows = isFirstOverallPage ? firstPageRows : otherFirstPageRows;
	if (positions.length <= initialRows) {
		return [positions];
	}
	const chunks = [];
	let start = 0;
	let pageRows = initialRows;
	while (start < positions.length) {
		const end = start + pageRows;
		chunks.push(positions.slice(start, end));
		start = end;
		pageRows = subsequentPageRows;
	}
	return chunks;
};

const PdfConfigurationsDocument = ({
	totalValuesSum,
	totalValues,
	oversizedGlassFields,
	configuration,
	contactInfo,
	customerInfo,
	showPrice,
	configuratorDataPricing,
	uiHandlerRef,
}) => {
	const { t } = useTranslation('pdfPage');
	const pdfCreationDate = useRef(getCurrentDate());
	const priceValidUntilDate = useRef(getPrevDayNextMonth());
	const dueDateByType = orderType => {
		switch (orderType) {
			case CONFIGURATION_TYPE.order:
				if (!configuration.deliveryByDate) {
					return null;
				}
				return t('orderDeliveryDateFmt').replace(
					'{}',
					dateToLocaleString(configuration.deliveryByDate)
				);
			case CONFIGURATION_TYPE.request:
				if (!configuration.quoteByDate) {
					return null;
				}
				return t('requestDueDateFmt').replace('{}', dateToLocaleString(configuration.quoteByDate));
			default:
				if (configuration.quoteByDate) {
					return t('requestDueDateFmt').replace(
						'{}',
						dateToLocaleString(configuration.quoteByDate)
					);
				}
				if (configuration.deliveryByDate) {
					return t('orderDeliveryDateFmt').replace(
						'{}',
						dateToLocaleString(configuration.deliveryByDate)
					);
				}
				return null;
		}
	};
	const dueDateText = dueDateByType(configuration.type);
	const hasPriceExpiration =
		showPrice &&
		// as soon as there'u a due date, we remove the price validity header since that's the last step before ordering/RFQ
		!dueDateText &&
		configuration.type !== CONFIGURATION_TYPE.order &&
		configuration.type !== CONFIGURATION_TYPE.request;
	const priceValidUntilText = hasPriceExpiration
		? t('priceValidUntilFmt').replace('{}', priceValidUntilDate.current)
		: null;
	const datasheets = useMemo(
		() =>
			configuration?.glassTypes.map(
				gd => gd && uiHandlerRef.current?.glassDB.getDatasheet(gd.flatGlassStructure, i18n.language)
			),
		[configuration, uiHandlerRef]
	);

	const firstOverallPageRows = 2;
	const otherFirstPageRows = 5;
	const subsequentPageRows = 20;

	const allPages = configuration.glassTypes.flatMap((glassData, glassIndex) => {
		const emptyRow = row =>
			row.every(
				item =>
					!(
						(item.name === 'length' || item.name === 'width' || item.name === 'quantity') &&
						item.value !== ''
					)
			);
		const positions = glassData.positions?.filter(o => !emptyRow(o.row)) || [];
		const isFirstOverallPage = glassIndex === 0;
		const chunks = splitPositions(
			positions,
			isFirstOverallPage,
			firstOverallPageRows,
			otherFirstPageRows,
			subsequentPageRows
		);
		return chunks.map((chunk, chunkIndex) => {
			const startIndex =
				chunkIndex === 0
					? 0
					: (isFirstOverallPage ? firstOverallPageRows : otherFirstPageRows) +
						(chunkIndex - 1) * subsequentPageRows;
			return {
				glassData,
				glassIndex,
				chunk,
				startIndex,
				isFirstPage: chunkIndex === 0,
				isLastPageForGlassType: chunkIndex === chunks.length - 1,
				isFirstOverallPage,
			};
		});
	});

	return (
		<Document style={styles.PDFContainer}>
			{allPages.map((pageInfo, pageIndex) => {
				const { glassData, glassIndex, chunk, startIndex, isFirstPage, isLastPageForGlassType } =
					pageInfo;
				const isFirstOverallPage = pageIndex === 0;
				const isLastOverallPage = pageIndex === allPages.length - 1;
				return (
					<Page style={styles.PDFContainer} size="A3" key={`${glassIndex}-${startIndex}`}>
						<View style={styles.section} wrap={false}>
							<View style={styles.sectionBlock}>
								<PdfHeader
									documentType={t(configuration.type)}
									creationDate={pdfCreationDate.current}
									priceValidUntilText={priceValidUntilText}
									dueDateText={dueDateText}
									contactInfo={contactInfo}
									customerInfo={customerInfo}
								/>
							</View>
							{isFirstOverallPage && (
								<>
									<View style={styles.sectionBlock}>
										<PdfConfigurationCode code={configuration.code} />
									</View>
									<View style={styles.sectionBlock}>
										<PdfYourReference
											reference={configuration.reference}
											buildingReference={configuration.buildingReference}
										/>
									</View>
								</>
							)}
							{isFirstPage && (
								<View style={styles.sectionBlock}>
									<View style={styles.glassConfigImageWlgState}>
										<Image
											src={glassData.screenshot ?? GlassPreviewImage}
											style={styles.glassConfigImage}
										/>
									</View>
									<PdfBlockHeading
										title={glassData.fireResistant}
										displayedBlockPosition={glassIndex + 1}
										iconName="gear"
										datasheetUrl={datasheets[glassIndex]}
									/>
									<PdfGlassConfigDetailFirst configDetails={glassData} />
									<PdfGlassConfigDetailSecond
										configDetails={glassData}
										showPrice={showPrice}
										priceVolumeCat={totalValuesSum.priceVolumeCat}
									/>
								</View>
							)}
							<View style={styles.sectionBlock} wrap={false}>
								<PdfGlassConfigTable
									positions={chunk}
									totalValues={totalValues[glassIndex]}
									oversizedGlassFields={
										oversizedGlassFields[glassIndex] ? [...[oversizedGlassFields[glassIndex]]] : []
									}
									glassIndex={glassIndex}
									startIndex={startIndex}
									showTotal={isLastPageForGlassType}
									showPrice={showPrice}
									priceVolumeCat={totalValuesSum.priceVolumeCat}
								/>
							</View>
							{isLastOverallPage && (
								<View style={styles.sectionBlock} wrap={false}>
									<PdfTotalInfo
										{...totalValuesSum}
										showPrice={showPrice}
										configuratorDataPricing={configuratorDataPricing}
									/>
								</View>
							)}
							{isLastOverallPage && configuration.uploadedDocuments.length ? (
								<PdfUploadedDocuments documentsList={configuration.uploadedDocuments} />
							) : null}
							<View style={styles.sectionBlockFooter} fixed>
								<Text
									style={styles.sectionBlockFooterText}
									render={({ pageNumber, totalPages }) => {
										const created = pdfCreationDate.current;
										const paging = t('pageFooterFmt')
											.replace('{pageNum}', pageNumber)
											.replace('{numPages}', totalPages);
										return `${created} - ${paging}`;
									}}
									fixed
								/>
							</View>
						</View>
					</Page>
				);
			})}
		</Document>
	);
};

export default PdfConfigurationsDocument;
